import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Logo } from "../Logo";
import { STEPS } from "./steps";
import { StepField } from "./StepField";
import { Summary } from "./Summary";

const KEY = "nexus-briefing-draft";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; } };

export const Briefing = ({ onClose }) => {
  const [answers, setAnswers] = useState(load);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [dir, setDir] = useState(1);
  const scroller = useRef(null);
  const total = STEPS.length;
  const isSummary = step === total;

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(answers)); }, [answers]);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  useEffect(() => { scroller.current?.scrollTo({ top: 0 }); }, [step]);

  const set = (k, v) => { setAnswers((a) => ({ ...a, [k]: v })); setError(""); };
  const goTo = (i) => { setDir(i > step ? 1 : -1); setError(""); setStep(i); };
  const next = (err) => { if (err) { setError(err); return; } goTo(step + 1); };
  const reset = () => { localStorage.removeItem(KEY); setAnswers({}); goTo(0); };
  const progress = Math.round((Math.min(step, total) / total) * 100);

  return (
    <motion.div className="fixed inset-0 z-[80] bg-[#050505]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} data-testid="briefing-modal" role="dialog" aria-modal="true" aria-label="Planejar orçamento">
      <div className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(212,175,90,0.12),transparent_60%)]" />
      <div className="relative h-full flex flex-col">
        <header className="flex items-center justify-between px-5 sm:px-10 h-16 lg:h-20 shrink-0">
          <Logo className="text-xl" />
          <span className="label hidden sm:block">Planejar orçamento</span>
          <button onClick={onClose} data-testid="briefing-close" aria-label="Fechar" className="grid place-items-center h-11 w-11 rounded-full bg-white/5 border border-white/10 hover:border-[#D4AF5A]/50 transition-colors"><X size={18} /></button>
        </header>
        <div className="px-5 sm:px-10 shrink-0">
          <div className="flex justify-between font-mono text-xs text-[#A7A7AD] mb-2">
            <span data-testid="briefing-progress-label">{isSummary ? "Resumo" : `Pergunta ${step + 1} de ${total}`}</span><span>{progress}%</span>
          </div>
          <div className="h-[2px] bg-white/10 rounded-full overflow-hidden">
            <motion.div className="h-full bg-[#D4AF5A]" animate={{ width: `${progress}%` }} transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }} data-testid="briefing-progress-bar" />
          </div>
        </div>
        <div ref={scroller} data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-10">
          <div className="mx-auto max-w-2xl py-10 lg:py-16">
            <motion.div key={step} initial={{ opacity: 0, x: dir * 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}>
              {isSummary
                ? <Summary answers={answers} onEdit={goTo} onReset={reset} />
                : <StepField step={STEPS[step]} index={step} answers={answers} set={set} error={error} onNext={next} />}
            </motion.div>
          </div>
        </div>
        {!isSummary && (
          <footer className="shrink-0 border-t border-white/5 px-5 sm:px-10 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex items-center justify-between gap-3 bg-[#050505]">
            <button onClick={() => goTo(step - 1)} disabled={step === 0} data-testid="briefing-back" className="btn-ghost h-12 px-5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-30 disabled:pointer-events-none">
              <ArrowLeft size={16} /> Voltar
            </button>
            <button form="briefing-step-form" type="submit" data-testid="briefing-next" className="btn-gold h-12 px-7 rounded-full text-sm font-semibold inline-flex items-center gap-2">
              {step === total - 1 ? <>Ver resumo <Check size={16} /></> : <>Continuar <ArrowRight size={16} /></>}
            </button>
          </footer>
        )}
      </div>
    </motion.div>
  );
};
